import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, X, Check } from "lucide-react";

export default function Exits() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/exits");
    setItems(data); setLoading(false);
  };
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); load(); }, []);

  const toggle = async (item, field) => {
    await api.put(`/exits/${item.id}`, { [field]: !item[field] });
    load();
  };

  const setStatus = async (item, status) => {
    await api.put(`/exits/${item.id}`, { status });
    load();
  };

  return (
    <div className="space-y-6" data-testid="exits-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Off-boarding</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Exit Management</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="initiate-exit-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Initiate Exit
        </button>
      </header>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="exits-grid">
          {items.length === 0 && <div className="text-sm text-stone-500">No active exit processes.</div>}
          {items.map((x) => (
            <div key={x.id} className="card-flat">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-semibold">{x.employee_name}</div>
                  <div className="text-xs text-stone-500">{x.designation} · {x.department_name}</div>
                </div>
                <select value={x.status} onChange={(e) => setStatus(x, e.target.value)}
                        data-testid={`exit-status-${x.id}`}
                        className="text-xs px-2 py-1 rounded border border-stone-200 bg-white">
                  {["Initiated", "In Progress", "Completed"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <Field label="Resigned on" v={x.resignation_date} />
                <Field label="Last Working Day" v={x.last_working_day} />
                <Field label="Notice Period" v={`${x.notice_period_days} days`} />
                <Field label="F&F Amount" v={formatINR(x.fnf_amount)} />
              </div>
              <div className="mt-3 text-xs italic text-stone-600">"{x.reason}"</div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Toggle on={x.asset_returned} onClick={() => toggle(x, "asset_returned")}
                        data-testid={`asset-returned-${x.id}`} label="Asset returned" />
                <Toggle on={x.knowledge_transfer_done} onClick={() => toggle(x, "knowledge_transfer_done")}
                        data-testid={`ktd-${x.id}`} label="KT done" />
              </div>
            </div>
          ))}
        </div>
      )}

      {show && <ExitModal employees={employees} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function Field({ label, v }) {
  return <div><div className="kpi-label">{label}</div><div className="text-sm font-medium mt-0.5">{v}</div></div>;
}

function Toggle({ on, onClick, label, ...rest }) {
  return (
    <button onClick={onClick} {...rest}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition ${on ? "bg-primary text-white border-primary" : "bg-white text-stone-600 border-stone-200"}`}>
      <Check className={`w-3.5 h-3.5 ${on ? "opacity-100" : "opacity-30"}`} /> {label}
    </button>
  );
}

function ExitModal({ employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_id: employees[0]?.id || "",
    resignation_date: new Date().toISOString().slice(0, 10),
    last_working_day: new Date(Date.now() + 30*86400000).toISOString().slice(0, 10),
    reason: "", notice_period_days: 30, fnf_amount: 0,
    asset_returned: false, knowledge_transfer_done: false, status: "Initiated",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/exits", { ...form, notice_period_days: Number(form.notice_period_days), fnf_amount: Number(form.fnf_amount) });
    setBusy(false); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="exit-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Initiate Exit Process</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <label className="col-span-2"><div className="kpi-label mb-1.5">Employee</div>
            <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.designation}</option>)}
            </select>
          </label>
          <In label="Resignation Date" type="date" value={form.resignation_date} onChange={(v) => setForm({ ...form, resignation_date: v })} />
          <In label="Last Working Day" type="date" value={form.last_working_day} onChange={(v) => setForm({ ...form, last_working_day: v })} />
          <In label="Notice Period (days)" type="number" value={form.notice_period_days} onChange={(v) => setForm({ ...form, notice_period_days: v })} />
          <In label="F&F Amount ₹" type="number" value={form.fnf_amount} onChange={(v) => setForm({ ...form, fnf_amount: v })} />
          <label className="col-span-2"><div className="kpi-label mb-1.5">Reason</div>
            <textarea value={form.reason} rows="3" onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </label>
          <button type="submit" disabled={busy} data-testid="save-exit-button"
                  className="col-span-2 py-2 mt-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type, value, onChange }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
